import { type PropsWithChildren, createContext, useContext } from 'react';

import { type InterviewQuestionsResponse } from '@/services/api';

import type { PageAdminInterviewRouteParams } from './PageAdminInterview.helper';
import { type useGetInterviewQuery } from './useAdminInterviewQueries';

export type DisplayMode = 'detail' | 'questions' | 'reports';

type PageAdminInterviewContextValue = PageAdminInterviewRouteParams & {
  interviewQuery: ReturnType<typeof useGetInterviewQuery>;
  handlePrevInterview: () => void;
  handleNextInterview: () => void;
  totalQuestionCount?: number;
  canGoNext: boolean;
  canGoPrev: boolean;
  displayMode: DisplayMode;
  setDisplayMode: (mode: DisplayMode) => void;
  isEditable: boolean;
  questionsData: InterviewQuestionsResponse | undefined;
};

const PageAdminInterviewContext =
  createContext<PageAdminInterviewContextValue | null>(null);

export const usePageAdminInterviewContext = () => {
  const context = useContext(PageAdminInterviewContext);
  if (!context) {
    throw new Error(
      'usePageAdminInterviewContext must be used within a PageAdminInterviewProvider'
    );
  }
  return context;
};

export const PageAdminInterviewProvider: React.FC<
  PropsWithChildren<PageAdminInterviewContextValue>
> = ({ children, ...value }) => {
  return (
    <PageAdminInterviewContext.Provider value={value}>
      {children}
    </PageAdminInterviewContext.Provider>
  );
};
